import React, { useState, useEffect } from "react";
import {
	Card,
	Table,
	Input,
	Select,
	Button,
	message,
	Typography,
	Tag,
	Row,
	Col,
	DatePicker,
} from "antd";
import {
	ExclamationCircleOutlined,
	FilterOutlined,
	PlusOutlined,
	DownloadOutlined,
} from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import AddDisciplinaryRecordModal from "./AddDisciplinaryRecordModal";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

const { Text } = Typography;
const { Option } = Select;

const ViewDisciplinaryRecords = () => {
	const isMobile = useIsMobile();
	const [records, setRecords] = useState([]);
	const [loading, setLoading] = useState(true);
	const [disciplinaryTypes, setDisciplinaryTypes] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [typeFilter, setTypeFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("all");
	const [dateRangeFilter, setDateRangeFilter] = useState(null);
	const [addModalVisible, setAddModalVisible] = useState(false);

	useEffect(() => {
		fetchDisciplinaryTypes();
		fetchAllDisciplinaryRecords();
	}, []);

	const fetchDisciplinaryTypes = async () => {
		try {
			const response = await useAxios.get(
				"/users/disciplinary-types/"
			);
			if (response.status === 200) {
				setDisciplinaryTypes(response.data);
			}
		} catch (error) {
			message.error("징계 유형을 불러오는데 실패했습니다.");
		}
	};

	const fetchAllDisciplinaryRecords = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/users/all-disciplinary-records/"
			);
			if (response.status === 200) {
				setRecords(response.data);
			}
		} catch (error) {
			message.error("징계 기록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleTypeFilterChange = (value) => {
		setTypeFilter(value);
	};

	const handleStatusFilterChange = (value) => {
		setStatusFilter(value);
	};

	const handleDateRangeFilterChange = (dates) => {
		setDateRangeFilter(dates);
	};

	const handleAddSuccess = () => {
		setAddModalVisible(false);
		fetchAllDisciplinaryRecords(); // Refresh the list
		message.success("징계 기록이 성공적으로 추가되었습니다.");
	};

	const getDisciplinaryColor = (type) => {
		switch (type) {
			case "출교":
				return "red";
			case "파면":
				return "volcano";
			case "정직":
				return "orange";
			case "감봉":
				return "gold";
			case "견책":
				return "lime";
			case "근신":
				return "blue";
			default:
				return "default";
		}
	};

	const getDisciplinaryStatus = (record) => {
		const startDate = dayjs(record.start_date);
		const endDate = startDate.add(record.period, "day");
		const today = dayjs();

		if (today.isBefore(startDate)) {
			return "예정";
		} else if (today.isAfter(endDate)) {
			return "완료";
		} else {
			return "진행중";
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "예정":
				return "orange";
			case "진행중":
				return "red";
			case "완료":
				return "green";
			default:
				return "default";
		}
	};

	const filteredRecords = records.filter((record) => {
		// Search filter
		if (searchText) {
			const searchValue =
				record.user_name?.toLowerCase() || "";
			if (!searchValue.includes(searchText.toLowerCase())) {
				return false;
			}
		}

		// Type filter
		if (typeFilter !== "all" && record.type !== typeFilter) {
			return false;
		}

		// Status filter
		if (statusFilter !== "all") {
			const recordStatus = getDisciplinaryStatus(record);
			if (recordStatus !== statusFilter) {
				return false;
			}
		}

		// Date range filter
		if (
			dateRangeFilter &&
			dateRangeFilter[0] &&
			dateRangeFilter[1]
		) {
			const recordDate = dayjs(record.resolution_date);
			const startDate = dateRangeFilter[0];
			const endDate = dateRangeFilter[1];
			if (
				!recordDate.isBetween(
					startDate,
					endDate,
					"day",
					"[]"
				)
			) {
				return false;
			}
		}

		return true;
	});

	const handleDownloadExcel = () => {
		try {
			// Prepare data for Excel
			const excelData = filteredRecords.map((record) => ({
				성도명: record.user_name || "-",
				생년월일: record.birthdate
					? dayjs(record.birthdate).format(
							"YYYY-MM-DD"
					  )
					: "-",
				분류: record.classification_name || "-",
				소속: record.affiliated_group || "-",
				징계유형: record.type || "-",
				결의일: record.resolution_date
					? dayjs(record.resolution_date).format(
							"YYYY-MM-DD"
					  )
					: "-",
				징계시작일: record.start_date
					? dayjs(record.start_date).format(
							"YYYY-MM-DD"
					  )
					: "-",
				징계기간: record.period
					? `${record.period}일`
					: "-",
				징계사유: record.reason || "-",
				상태: getDisciplinaryStatus(record),
			}));

			// Convert to CSV format
			const headers = Object.keys(excelData[0] || {});
			const csvContent = [
				headers.join(","),
				...excelData.map((row) =>
					headers
						.map((header) => {
							const value =
								row[header] ||
								"";
							// Escape commas and quotes in the value
							if (
								value.includes(
									","
								) ||
								value.includes(
									'"'
								) ||
								value.includes(
									"\n"
								)
							) {
								return `"${value.replace(
									/"/g,
									'""'
								)}"`;
							}
							return value;
						})
						.join(",")
				),
			].join("\n");

			// Create and download file
			const blob = new Blob(["\uFEFF" + csvContent], {
				type: "text/csv;charset=utf-8;",
			});
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute(
				"download",
				`징계기록_${dayjs().format(
					"YYYY-MM-DD_HH-mm"
				)}.csv`
			);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			message.success(
				"엑셀 파일이 성공적으로 다운로드되었습니다."
			);
		} catch (error) {
			message.error("엑셀 파일 다운로드에 실패했습니다.");
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const columns = [
		{
			title: "성도명",
			dataIndex: "user_name",
			key: "user_name",
			sorter: (a, b) =>
				(a.user_name || "").localeCompare(
					b.user_name || ""
				),
			render: (name) => <Text strong>{name || "-"}</Text>,
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			sorter: (a, b) =>
				dayjs(a.birthdate || 0).unix() -
				dayjs(b.birthdate || 0).unix(),
			render: (date) =>
				date ? dayjs(date).format("YYYY-MM-DD") : "-",
		},
		{
			title: "분류",
			dataIndex: "classification_name",
			key: "classification_name",
			sorter: (a, b) =>
				(a.classification_name || "").localeCompare(
					b.classification_name || ""
				),
			render: (classification) => (
				<Tag
					color={getClassificationColor(
						classification
					)}>
					{classification || "-"}
				</Tag>
			),
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
			sorter: (a, b) =>
				(a.affiliated_group || "").localeCompare(
					b.affiliated_group || ""
				),
			render: (group) => <Text>{group || "-"}</Text>,
		},
		{
			title: "징계 유형",
			dataIndex: "type",
			key: "type",
			sorter: (a, b) =>
				(a.type || "").localeCompare(b.type || ""),
			render: (type) => (
				<Tag color={getDisciplinaryColor(type)}>
					{type}
				</Tag>
			),
		},
		{
			title: "결의일",
			dataIndex: "resolution_date",
			key: "resolution_date",
			sorter: (a, b) =>
				dayjs(a.resolution_date).unix() -
				dayjs(b.resolution_date).unix(),
			render: (date) => dayjs(date).format("YYYY-MM-DD"),
			defaultSortOrder: "descend",
		},
		{
			title: "징계 시작일",
			dataIndex: "start_date",
			key: "start_date",
			sorter: (a, b) =>
				dayjs(a.start_date).unix() -
				dayjs(b.start_date).unix(),
			render: (date) => dayjs(date).format("YYYY-MM-DD"),
		},
		{
			title: "징계 기간",
			dataIndex: "period",
			key: "period",
			sorter: (a, b) => a.period - b.period,
			render: (period) => `${period}일`,
		},
		{
			title: "징계 사유",
			dataIndex: "reason",
			key: "reason",
			render: (reason) => (
				<Text
					style={{
						maxWidth: 200,
						display: "block",
					}}>
					{reason || "-"}
				</Text>
			),
		},
		{
			title: "상태",
			key: "status",
			sorter: (a, b) => {
				const statusA = getDisciplinaryStatus(a);
				const statusB = getDisciplinaryStatus(b);
				return statusA.localeCompare(statusB);
			},
			render: (_, record) => {
				const status = getDisciplinaryStatus(record);
				return (
					<Tag color={getStatusColor(status)}>
						{status}
					</Tag>
				);
			},
		},
	];

	return (
		<div className="page" style={{ padding: "24px" }}>
			<PageHeader
				title={
					<>
						<ExclamationCircleOutlined /> 징계 기록 관리
					</>
				}
				subtitle="모든 성도의 징계 기록을 조회하고 관리할 수 있습니다."
			/>
			<Card className="surface-card">

				{/* Search and Filter Section */}
				<div
					style={{
						marginTop: 24,
						marginBottom: 16,
					}}>
					<Row gutter={[16, 16]} align="middle">
						<Col xs={24} sm={12} md={5}>
							<Input.Search
								placeholder="성도명으로 검색"
								allowClear
								onSearch={
									handleSearch
								}
								style={{
									width: "100%",
								}}
							/>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Select
								placeholder="징계 유형"
								value={
									typeFilter
								}
								onChange={
									handleTypeFilterChange
								}
								style={{
									width: "100%",
								}}
								allowClear>
								<Option value="all">
									전체
									유형
								</Option>
								{disciplinaryTypes.map(
									(
										type
									) => (
										<Option
											key={
												type.id
											}
											value={
												type.name
											}>
											<Tag
												color={getDisciplinaryColor(
													type.name
												)}>
												{
													type.name
												}
											</Tag>
										</Option>
									)
								)}
							</Select>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Select
								placeholder="징계 상태"
								value={
									statusFilter
								}
								onChange={
									handleStatusFilterChange
								}
								style={{
									width: "100%",
								}}
								allowClear>
								<Option value="all">
									전체
									상태
								</Option>
								<Option value="예정">
									<Tag color="orange">
										예정
									</Tag>
								</Option>
								<Option value="진행중">
									<Tag color="red">
										진행중
									</Tag>
								</Option>
								<Option value="완료">
									<Tag color="green">
										완료
									</Tag>
								</Option>
							</Select>
						</Col>
						<Col xs={24} sm={12} md={5}>
							<DatePicker.RangePicker
								inputReadOnly={isMobile}
								placeholder={[
									"시작일",
									"종료일",
								]}
								value={
									dateRangeFilter
								}
								onChange={
									handleDateRangeFilterChange
								}
								style={{
									width: "100%",
								}}
								format="YYYY-MM-DD"
							/>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Button
								type="primary"
								icon={
									<PlusOutlined />
								}
								onClick={() =>
									setAddModalVisible(
										true
									)
								}
								style={{
									width: "100%",
								}}>
								징계 기록 추가
							</Button>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Button
								icon={
									<DownloadOutlined />
								}
								onClick={
									handleDownloadExcel
								}
								style={{
									width: "100%",
								}}>
								엑셀 다운로드
							</Button>
						</Col>
						<Col xs={24} sm={12} md={2}>
							<Button
								icon={
									<FilterOutlined />
								}
								onClick={() => {
									setSearchText(
										""
									);
									setTypeFilter(
										"all"
									);
									setStatusFilter(
										"all"
									);
									setDateRangeFilter(
										null
									);
								}}
								style={{
									width: "100%",
								}}>
								필터 초기화
							</Button>
						</Col>
					</Row>
				</div>

				{/* Records Table */}
				<Table
					columns={columns}
					dataSource={filteredRecords}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 20,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} / 총 ${total}개`,
					}}
					locale={{
						emptyText: "징계 기록이 없습니다.",
					}}
					scroll={{ x: 1600 }}
				/>
			</Card>

			{/* Add Disciplinary Record Modal */}
			<AddDisciplinaryRecordModal
				visible={addModalVisible}
				onCancel={() => setAddModalVisible(false)}
				onSuccess={handleAddSuccess}
			/>
		</div>
	);
};

export default ViewDisciplinaryRecords;
